import React, { useCallback, useEffect, useState } from "react";
import {
    Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Badge,
} from "reactstrap";
import axios from "axios";
import { API } from "../../../api/urls";

/**
 * ScheduledReportPreviewModal – Raporu e-posta beklemeden panelde görüntüler.
 *
 * Gösterilen satırlar Excel'deki 25 sütunun tamamı değil; modalda okunaksız
 * olurdu. Çekirdek alanlar burada, ayrıntının tamamı indirilen dosyada.
 *
 * Props
 * ─────────────────────────────────────────────────────────
 * @param {boolean}  isOpen   – Modal açık mı?
 * @param {Function} toggle   – Modal'ı kapat/aç
 * @param {Object}   report   – Görüntülenecek rapor tanımı
 * @param {Function} onExport – "İndir" tıklandığında çalışır
 */
const ScheduledReportPreviewModal = ({ isOpen, toggle, report, onExport }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const fmtMoney = (v) =>
        v == null ? "-" : `₺${Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const fmtDate = (v) => {
        if (!v) return "-";
        const d = new Date(v);
        return isNaN(d) ? "-" : d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
    };

    const fmtDay = (v) => {
        if (!v) return "-";
        const d = new Date(v);
        return isNaN(d) ? "-" : d.toLocaleDateString("tr-TR");
    };

    const statusBadge = (s) => {
        const map = {
            SUCCESS: { color: "success", label: "Başarılı" },
            PENDING: { color: "warning", label: "Beklemede" },
            FAILED: { color: "danger", label: "Başarısız" },
            REFUNDED: { color: "secondary", label: "İade" },
            PARTIALLY_REFUNDED: { color: "secondary", label: "Kısmi İade" },
        };
        const m = map[s] || { color: "light", label: s || "-" };
        return <Badge color={m.color}>{m.label}</Badge>;
    };

    const load = useCallback(async () => {
        if (!report?.id) return;
        setLoading(true);
        setError(null);
        try {
            const { data: res } = await axios.get(API.scheduled_report_preview(report.id), {
                withCredentials: true,
            });
            setData(res?.data || null);
        } catch (e) {
            setError(e?.response?.data?.message || "Rapor önizlemesi alınamadı.");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [report?.id]);

    useEffect(() => {
        if (isOpen) load();
    }, [isOpen, load]);

    if (!report) return null;

    const rows = data?.rows || [];

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
            <ModalHeader toggle={toggle}>Rapor Önizleme</ModalHeader>

            <ModalBody>
                {loading && (
                    <div className="text-center py-5">
                        <Spinner /> <div className="mt-2 text-muted">Rapor hazırlanıyor…</div>
                    </div>
                )}

                {!loading && error && (
                    <div className="alert alert-danger mb-0">{error}</div>
                )}

                {!loading && !error && data && (
                    <>
                        {/* Raporun hangi aralığı kapsadığı hiçbir yerde yazmıyordu;
                            sıklıktan türetildiği için kullanıcı tahmin etmek zorundaydı. */}
                        <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
                            <span>
                                <strong>Kapsanan dönem:</strong>{" "}
                                {fmtDay(data.periodStart)} – {fmtDay(data.periodEnd)}
                            </span>
                            <span className="text-muted">
                                Oluşturulma: {fmtDate(data.generatedAt)}
                            </span>
                        </div>

                        <div className="row g-2 mb-3">
                            {[
                                { label: "Başarılı", count: data.successCount, amount: data.successAmount, color: "#198754" },
                                { label: "Beklemede", count: data.pendingCount, amount: data.pendingAmount, color: "#E56A22" },
                                { label: "Başarısız", count: data.failedCount, amount: data.failedAmount, color: "#dc3545" },
                            ].map((c) => (
                                <div className="col-12 col-md-4" key={c.label}>
                                    <div className="border rounded p-2 h-100">
                                        <div style={{ color: c.color, fontWeight: 600 }}>{c.label}</div>
                                        <div className="fs-5">{c.count ?? 0} işlem</div>
                                        <div className="text-muted">{fmtMoney(c.amount)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {rows.length === 0 ? (
                            <div className="alert alert-info mb-0">
                                Bu dönemde rapor ölçütlerinize uyan kayıt bulunamadı.
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-sm align-middle">
                                        <thead>
                                            <tr>
                                                <th>İşlem No</th>
                                                <th>Tarih</th>
                                                <th>Cari</th>
                                                <th>Kullanıcı</th>
                                                <th className="text-end">Tutar</th>
                                                <th className="text-center">Taksit</th>
                                                <th>Durum</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((r) => (
                                                <tr key={r.merchantOid}>
                                                    <td style={{ fontFamily: "monospace", fontSize: ".8rem" }}>{r.merchantOid}</td>
                                                    <td>{fmtDate(r.paymentDate)}</td>
                                                    <td>{r.accountName || "-"}</td>
                                                    <td>{r.userName || "-"}</td>
                                                    <td className="text-end">{fmtMoney(r.amount)}</td>
                                                    <td className="text-center">{r.installmentCount || 1}</td>
                                                    <td>{statusBadge(r.status)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {data.truncated && (
                                    <div className="text-muted small">
                                        Toplam {data.totalCount} kayıttan ilk {rows.length} tanesi gösteriliyor.
                                        Tamamı için raporu indirin.
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </ModalBody>

            <ModalFooter>
                <Button color="secondary" outline onClick={toggle}>Kapat</Button>
                <Button color="primary" onClick={() => onExport(report)} disabled={loading}>
                    <i className="bx bx-download me-1" />
                    Excel indir
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ScheduledReportPreviewModal;
